import React, { FC, useCallback, useState, useEffect, useRef } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { Grid, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import AddIcon from '@mui/icons-material/Add'
import { toast } from 'react-toastify'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { OnboardingUserDto } from '@sparling/financial-common'
import { USER_ROLES } from '../../../../constants/userRoles'
import { TitleOnBoarding } from '../../../../components/TitleOnBoarding'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { CInputField } from '../../../../components/CustomInputs/CInputField'
import { setStepperFormData } from '../../../../services/setStepperFormData'
import { getStepperFormData } from '../../../../services/getStepperFormData'
import { HelpMatrixPopover } from '../../../../components/HelpMatrixPopover'
import { InputSelect } from '../../../../components/CustomInputs/InputSelect'
import { AddAnotherTable } from '../../../../components/AddAnotherTable'
import { useRest } from '../../../../components/Context'
import useDebounce from '../../../../hooks/useDebounce'
import { errorMessage } from '../../../../constants/errorMessages'

const resolver = classValidatorResolver(OnboardingUserDto)

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    margin: '0 auto',
    maxWidth: 1184,
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 'bold',
  },
  input: {
    width: 300,
    margin: '12px 16px 24px 16px',
    '& label': {
      fontSize: 14,
      color: theme.palette.primary.dark,
    },
    '& .MuiInput-underline:after': {
      borderBottom: `1px solid ${theme.palette.info.light}`,
    },
  },
  select: {
    width: 264,
    marginBottom: 40,
    marginRight: 12,
    '& .MuiSelect-selectMenu': {
      textAlign: 'left',
    },
  },
  btn: {
    color: theme.palette.info.light,
    marginTop: 20,
    marginBottom: 32,
  },
  btnIcon: {
    marginRight: 12,
  },
  selectItem: {
    textAlign: 'left',
  },

  bold: {
    fontWeight: 'bold',
  },
  addInput: {
    color: '#584DE2',
    cursor: 'pointer',
    '&:hover': {
      color: '#9b95ee',
    },
  },
  addInputDisabled: {
    color: '#9b95ee',
    cursor: 'disabled',
    pointerEvents: 'none',
  },
  formContainer: {
    width: '632px',
    marginTop: '80px',
  },
  nextButton: {
    marginTop: '73px',
  },
  fieldPopover: {
    marginLeft: 32,
  },
  popover: {
    width: 24,
    height: 24,
    marginLeft: 12,
    marginBottom: 24,
  },
  addInputLabel: {
    fontWeight: 'bold',
  },
}))

interface AddUserItem {
  firstName: string
  lastName: string
  email: string
  role: string
}

interface IAddUser {
  title: string
}

export const AddUserOnBoarding: FC<IAddUser> = ({ title }) => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const [activeStep] = useState(7)
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [editIndex, setEditIndex] = useState<number | undefined>()
  const [formDisabled, seFormDisabled] = useState<boolean>(false)
  const [formSubmitDisabled, seFormSubmitDisabled] = useState<boolean>(true)
  const [isDisabled, setDisabled] = useState<boolean>(false)
  const [isFilled, seIsFilled] = useState<boolean>(false)
  const [Email, setEmail] = useState<string | undefined>()
  // eslint-disable-next-line
  const [formStorage, seFormStorage] = useState<any>([])
  const formRef = useRef<HTMLFormElement>(null)
  const debouncedValue = useDebounce<string | undefined>(Email, 200)
  const { uniqKey } = useParams() as { [key: string]: string }
  const { rxios } = useRest()

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      executingTrades: true,
    },
    resolver,
  })

  const { handleSubmit, setValue, formState, watch } = formMethods

  const emailVerification = useCallback(
    (email: string | undefined) => {
      rxios
        .post('company/onboarding/email-verification', {
          email,
          hash: uniqKey,
        })
        .subscribe(
          // eslint-disable-next-line
          (next: any) => {
            setDisabled(false)
          },
          (error) => {
            toast.error(error.response.data.message)
            setDisabled(true)
          },
        )
      // eslint-disable-next-line
    },
    [rxios, uniqKey],
  )

  useEffect(() => {
    if (/^[a-zA-Z0-9._-]+@(?:[a-zA-Z0-9-]+\.)+([a-zA-Z0-9-]{2,})+$/.test(Email as string)) {
      if (
        Email === formStorage?.company?.email ||
        // eslint-disable-next-line
        formStorage?.ubo?.find((item: any) => item.email === Email) ||
        // eslint-disable-next-line
        formStorage?.director?.find((item: any) => item.email === Email)
      ) {
        toast.error(errorMessage.EMAIL_IS_ALREADY_REGISTERED)
        setDisabled(true)
      } else {
        emailVerification(Email)
      }
    }
    // eslint-disable-next-line
  }, [debouncedValue])

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'email') setEmail(value[name])
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const updateFormStorage = useCallback(() => {
    const storage = getStepperFormData()
    seFormStorage(storage)
  }, [])

  const addAnotherUser = () => {
    const getNewValue = formMethods.getValues()
    if (getNewValue.firstName && getNewValue.lastName && getNewValue.email) {
      if (!isEdit) {
        setStepperFormData(
          // eslint-disable-next-line no-prototype-builtins
          !formStorage.hasOwnProperty('user')
            ? { ...formStorage, user: [getNewValue] }
            : { ...formStorage, user: [...formStorage.user, getNewValue] },
        )
      } else {
        const storage = getStepperFormData()
        const newFormStorage = Object.assign(storage)
        const { user } = newFormStorage
        // eslint-disable-next-line
        const newUser = user.filter((item: any, i: number) => i !== editIndex)
        newUser.push(getNewValue)
        setStepperFormData({ ...formStorage, user: [...newUser] })
      }
      updateFormStorage()
      formMethods.reset({
        firstName: '',
        lastName: '',
        email: '',
        role: '',
      })
      setIsEdit(false)
      setEditIndex(undefined)
      seFormDisabled(true)
      seIsFilled(false)
    }
  }

  const formValid = useCallback(() => {
    const getNewValue = formMethods.getValues()
    const { firstName, lastName, email, role } = getNewValue
    if (firstName.length && lastName.length && email.length && role.length) {
      seIsFilled(true)
    } else {
      seIsFilled(false)
    }
  }, [formMethods])

  useEffect(() => {
    formRef?.current?.addEventListener('change', formValid)

    if (isFilled) {
      const { firstName, lastName, email, role } = formState.errors
      if (!firstName && !lastName && !email && !role) {
        seFormDisabled(false)
      }
    } else {
      seFormDisabled(true)
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      formRef?.current?.removeEventListener('change', formValid)
    }
  }, [isFilled, formState, formValid])

  useEffect(() => {
    updateFormStorage()
  }, [updateFormStorage])

  useEffect(() => {
    if (formStorage?.user?.length) {
      seFormSubmitDisabled(false)
    } else {
      seFormSubmitDisabled(formDisabled)
    }
  }, [formStorage, formMethods, formDisabled])

  const companyOnboardingSave = useCallback(() => {
    const storage = getStepperFormData()
    rxios.post(`company/onboarding/save/${uniqKey}`, { ...storage }).subscribe(
      // eslint-disable-next-line
      (next: any) => {
        if (next.success) {
          history.push({
            ...location,
            state: {
              activeStep,
            },
          })
        }
      },
      (error) => {
        toast.error(error.response.data.message)
      },
    )
  }, [activeStep, history, location, rxios, uniqKey])

  const onSubmit = useCallback(
    (newValue) => {
      setStepperFormData(
        // eslint-disable-next-line no-prototype-builtins
        !formStorage.hasOwnProperty('user')
          ? { ...formStorage, user: [newValue] }
          : { ...formStorage, user: [...formStorage.user, newValue] },
      )
      companyOnboardingSave()
    },
    // eslint-disable-next-line
    [activeStep, history, formStorage],
  )

  const onEdit = useCallback(
    (index: number) => {
      const getNewValue = formMethods.getValues()
      // eslint-disable-next-line
      let newFormStorage = []
      if (getNewValue.firstName && getNewValue.lastName && getNewValue.email && getNewValue.role) {
        const storage = getStepperFormData()
        newFormStorage = Object.assign(storage)
      } else {
        newFormStorage = Object.assign(formStorage)
      }
      const { user } = newFormStorage
      const { firstName, lastName, email, role } = user[index]
      setValue('firstName', firstName, { shouldValidate: true })
      setValue('lastName', lastName, { shouldValidate: true })
      setValue('email', email, { shouldValidate: true })
      setValue('role', role, { shouldValidate: false })
      setIsEdit(true)
      setEditIndex(index)
      seFormDisabled(false)
      seIsFilled(true)
    },
    [formStorage, setValue, formMethods],
  )

  const onDelete = useCallback(
    (index: number) => {
      const newFormStorage = Object.assign(formStorage)
      const { user } = newFormStorage
      // eslint-disable-next-line
      const newUser = user.filter((item: any, i: number) => i !== index)
      setStepperFormData({ ...formStorage, user: newUser })
      updateFormStorage()
      setIsEdit(false)
      setEditIndex(undefined)
    },
    [formStorage, updateFormStorage],
  )

  const onSubmitForm = useCallback(
    // eslint-disable-next-line
    (e: any) => {
      e.preventDefault()
      if (formStorage?.user?.length) {
        const getNewValue = formMethods.getValues()
        if (!formDisabled) {
          handleSubmit(onSubmit)()
        } else if (
          (formDisabled && getNewValue.firstName) ||
          (formDisabled && getNewValue.lastName) ||
          (formDisabled && getNewValue.email) ||
          (formDisabled && getNewValue.role)
        ) {
          handleSubmit(onSubmit)()
        } else {
          companyOnboardingSave()
        }
      } else {
        handleSubmit(onSubmit)()
      }
    },
    [formMethods, formStorage, handleSubmit, onSubmit, companyOnboardingSave, formDisabled],
  )

  return (
    <>
      <FormProvider {...formMethods}>
        <form ref={formRef} onSubmit={onSubmitForm}>
          <Grid className={classes.container} container direction="column" alignItems="center">
            <TitleOnBoarding leftArrow title={title} activeStep={activeStep} />
            <Grid item container className={classes.formContainer} direction="column" alignItems={'center'}>
              <Grid item container justifyContent={'space-between'}>
                <CInputField name={'firstName'} label={'First Name'} />
                <CInputField name={'lastName'} label={'Last Name'} />
              </Grid>
              <Grid
                item
                container
                justifyContent={'space-between'}
                alignItems={'center'}
                direction={'row'}
                wrap={'nowrap'}
              >
                <Grid item>
                  <CInputField name={'email'} label={'User Email'} isCompanyEmail />
                </Grid>
                <Grid item container justifyContent={'flex-end'} className={classes.fieldPopover}>
                  <InputSelect
                    inputName={'role'}
                    width={264}
                    labelName={'User Role'}
                    enums={USER_ROLES}
                    onBlur={formValid}
                  />
                </Grid>
                <Grid item container className={classes.popover} justifyContent={'center'} alignItems={'center'}>
                  <HelpMatrixPopover />
                </Grid>
              </Grid>
              <Grid
                className={classes.btn}
                container
                direction={'row'}
                justifyContent={'flex-end'}
                alignItems={'center'}
                wrap={'nowrap'}
                // onClick={addAnotherUser}
              >
                <Grid
                  item
                  container
                  xs={3}
                  className={formDisabled || isDisabled ? classes.addInputDisabled : classes.addInput}
                  alignItems={'center'}
                  justifyContent={'flex-end'}
                  direction={'row'}
                  onClick={addAnotherUser}
                >
                  <AddIcon />
                  <Typography className={classes.addInputLabel} component={'span'} variant={'body2'}>
                    Add another user
                  </Typography>
                </Grid>
                {/* <AddOutlinedIcon className={classes.btnIcon} /> */}
                {/* <div>Add another user</div> */}
              </Grid>
              <Grid>
                <hr style={{ color: '#ebebeb', opacity: 0.6 }} />
                {/* eslint-disable-next-line no-prototype-builtins */}
                {formStorage.hasOwnProperty('user') &&
                  formStorage.user.map((item: AddUserItem, i: number) => {
                    return (
                      <AddAnotherTable
                        key={i}
                        fullName={`${item.firstName} ${item.lastName}`}
                        email={item.email}
                        role={item.role}
                        onEdit={() => onEdit(i)}
                        onDelete={() => onDelete(i)}
                        hidden={editIndex === i}
                      />
                    )
                  })}
              </Grid>
            </Grid>
            <GreenButton
              disabled={formSubmitDisabled || isDisabled}
              type="submit"
              width={321}
              mt={64}
              mb={247}
              text="SUBMIT"
            />
          </Grid>
        </form>
      </FormProvider>
    </>
  )
}
